import { NO_TARGETS, type NutritionTargets } from './nutrition';
import { ACTIVITY_LEVELS, type ActivityLevel } from './energyEstimate';

/**
 * ⭐ Nutrition phase 2 (owner, 2026-10-09): direction + dated target VERSIONS.
 * Pure helpers — the store reads and writes; this decides what is current and what a
 * saved direction means.
 */

/** Where the calories in a version came from — shown beside the number. */
export type CalorieSource = 'estimate' | 'estimate_adjusted' | 'user' | 'legacy';
export const CALORIE_SOURCE_LABEL: Record<CalorieSource, string> = {
  estimate: 'estimated (2023 DRI, maintenance)',
  estimate_adjusted: 'your number, based on the estimate',
  user: 'entered by you',
  legacy: 'set earlier',
};

/** One confirmed choice (`nutrition_target_versions`, insert-only). */
export interface TargetVersion {
  id: string;
  kind: 'numbers' | 'none';
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fat: number | null;
  calorieSource: CalorieSource | null;
  method: string | null;
  /** What the estimate used (age, sex, height, weight + its date, activity, the estimate). */
  inputs: Record<string, unknown>;
  confirmedAt: string;
  effectiveFrom: string; // YYYY-MM-DD; '1970-01-01' = "set before versions were kept"
}

export const LEGACY_EFFECTIVE = '1970-01-01';

/**
 * The version in effect on `date`: the newest by effective date (ties → the later
 * confirmation). Null when none applies yet.
 */
export function currentVersion(versions: ReadonlyArray<TargetVersion>, date: string): TargetVersion | null {
  let best: TargetVersion | null = null;
  for (const v of versions) {
    if (v.effectiveFrom > date) continue;
    if (!best || v.effectiveFrom > best.effectiveFrom || (v.effectiveFrom === best.effectiveFrom && v.confirmedAt > best.confirmedAt)) best = v;
  }
  return best;
}

/** The numbers a version sets (a "no numbers" version sets none). */
export function versionTargets(v: TargetVersion | null): NutritionTargets {
  if (!v || v.kind === 'none') return { ...NO_TARGETS };
  return { calories: v.calories, protein: v.protein, carbs: v.carbs, fat: v.fat };
}

/**
 * ⭐ The targets a DAY is measured against (owner, 2026-10-21 — "honest numbers"): the version
 * in force on that date, so last week is never re-judged by a target set this week. A day
 * before the first version has none (no line to fall short of). With no versions at all —
 * never set, or they failed to load (the store says so beside the targets) — `fallback`, the
 * store's current `targets`, is the best there is.
 */
export function targetsOn(versions: ReadonlyArray<TargetVersion>, fallback: NutritionTargets, date: string): NutritionTargets {
  if (!versions.length) return fallback;
  return versionTargets(currentVersion(versions, date));
}

// ── direction ──────────────────────────────────────────────────────────────

export type Intention = 'general_health' | 'muscle' | 'weight' | 'own';
export const INTENTIONS: { value: Intention; label: string }[] = [
  { value: 'general_health', label: 'General health' },
  { value: 'muscle', label: 'Muscle development' },
  { value: 'weight', label: 'Weight management' },
  { value: 'own', label: 'In my own words' },
];

export type Restriction =
  | 'vegetarian' | 'vegan' | 'pescatarian' | 'halal' | 'kosher' | 'gluten_free' | 'dairy_free'
  | 'nut_allergy' | 'shellfish_allergy' | 'egg_allergy' | 'soy_allergy';
export const RESTRICTIONS: { value: Restriction; label: string }[] = [
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'pescatarian', label: 'Pescatarian' },
  { value: 'halal', label: 'Halal' },
  { value: 'kosher', label: 'Kosher' },
  { value: 'gluten_free', label: 'Gluten-free' },
  { value: 'dairy_free', label: 'Dairy-free' },
  { value: 'nut_allergy', label: 'Nut allergy' },
  { value: 'shellfish_allergy', label: 'Shellfish allergy' },
  { value: 'egg_allergy', label: 'Egg allergy' },
  { value: 'soy_allergy', label: 'Soy allergy' },
];

// ⚠️ Budget was dropped (owner, 2026-10-17), and cooking time + kitchen went with Starter
// meals (owner, 2026-10-18) — nothing else read them. Stored values are ignored.
/**
 * ⭐ The weight goal the targets start from (owner, 2026-10-17): the steady-weight
 * estimate, or 250 / 500 kcal a day under or over it. The PERSON chooses; their
 * direction only pre-selects (`suggestedGoal`).
 */
export type WeightGoal = 'keep' | 'lose_250' | 'lose_500' | 'gain_250' | 'gain_500';
export const WEIGHT_GOALS: { value: WeightGoal; label: string; hint: string }[] = [
  { value: 'lose_500', label: 'Lose', hint: '500 kcal a day under the estimate' },
  { value: 'lose_250', label: 'Lose slowly', hint: '250 kcal a day under the estimate' },
  { value: 'keep', label: 'Keep a steady weight', hint: 'About what you burn' },
  { value: 'gain_250', label: 'Gain slowly', hint: '250 kcal a day over the estimate' },
  { value: 'gain_500', label: 'Gain', hint: '500 kcal a day over the estimate' },
];
export const GOAL_DELTA: Record<WeightGoal, number> = { lose_500: -500, lose_250: -250, keep: 0, gain_250: 250, gain_500: 500 };

export type Tracking = 'numbers' | 'routine';
export const TRACKINGS: { value: Tracking; label: string; hint: string }[] = [
  { value: 'numbers', label: 'Track numbers', hint: 'Calories and macros against targets.' },
  { value: 'routine', label: 'Routine without numbers', hint: 'Log what you eat; no calorie targets.' },
];

/** `user_preferences.nutrition_direction` — written whole. */
export interface NutritionDirection {
  version: 1;
  intention: Intention | null;
  /** Free text when intention is "own" (kept, not shown, otherwise). */
  intentionText: string;
  restrictions: Restriction[];
  restrictionsOther: string;
  /** Foods avoided or preferred. */
  preferences: string;
  tracking: Tracking | null;
  /** The activity category the estimate uses — chosen in the Direction & targets wizard. */
  activity: ActivityLevel | null;
  /** The weight goal the targets start from (2026-10-17). */
  weightGoal: WeightGoal | null;
  updatedAt: string | null;
}

export const EMPTY_DIRECTION: NutritionDirection = {
  version: 1, intention: null, intentionText: '', restrictions: [], restrictionsOther: '', preferences: '',
  tracking: null, activity: null, weightGoal: null, updatedAt: null,
};

const oneOf = <T extends string>(v: unknown, opts: readonly { value: T }[] | readonly T[]): T | null => {
  const values = (opts as readonly unknown[]).map(o => (typeof o === 'string' ? o : (o as { value: T }).value));
  return typeof v === 'string' && values.includes(v) ? (v as T) : null;
};
const text = (v: unknown, max: number) => (typeof v === 'string' ? v.slice(0, max) : '');

/** Read a stored direction defensively: unknown values are dropped, never guessed. */
export function readDirection(raw: unknown): NutritionDirection | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const restrictions = Array.isArray(o.restrictions)
    ? [...new Set(o.restrictions.map(r => oneOf<Restriction>(r, RESTRICTIONS)).filter((r): r is Restriction => r !== null))]
    : [];
  return {
    version: 1,
    intention: oneOf<Intention>(o.intention, INTENTIONS),
    intentionText: text(o.intentionText, 280),
    restrictions,
    restrictionsOther: text(o.restrictionsOther, 280),
    preferences: text(o.preferences, 500),
    tracking: oneOf<Tracking>(o.tracking, TRACKINGS),
    activity: oneOf<ActivityLevel>(o.activity, ACTIVITY_LEVELS),
    weightGoal: oneOf<WeightGoal>(o.weightGoal, WEIGHT_GOALS),
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : null,
  };
}

/** A direction with something chosen (the Foundation's step-1 evidence, phase 3). */
export function directionSaved(d: NutritionDirection | null): boolean {
  return !!d && (d.intention !== null || d.tracking !== null);
}

/**
 * Why the current targets may need a look — never applied by itself. A newer weight
 * reading than the one the estimate used, or a direction changed after the targets.
 */
export function reviewReasons(v: TargetVersion | null, d: NutritionDirection | null, latestWeightAt: string | null): string[] {
  if (!v || v.kind === 'none') return [];
  const out: string[] = [];
  const usedWeightAt = typeof v.inputs?.weightRecordedAt === 'string' ? (v.inputs.weightRecordedAt as string) : null;
  if (v.calorieSource && v.calorieSource.startsWith('estimate') && usedWeightAt && latestWeightAt && latestWeightAt > usedWeightAt) {
    out.push('You’ve logged a new weight since these targets were set.');
  }
  if (d?.updatedAt && d.updatedAt > v.confirmedAt) out.push('Your direction changed after these targets were set.');
  return out;
}
